import React, { useState } from 'react';
import { format, addHours } from 'date-fns';
import { Loader2, Phone, MessageCircle, Video, CheckSquare, Bell, Search, X, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useContactSearch } from '@/hooks/useContactSearch';
import { useDealsSearch } from '@/hooks/useDealsSearch';
import { useCalendarEventMutations } from '@/hooks/useCalendarEvents';
import { useQueryClient, useQuery } from '@tanstack/react-query';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export type TaskItemType = 'meeting_video' | 'call' | 'whatsapp' | 'task' | 'lembrete';

interface TaskFormModalProps {
  open: boolean;
  onClose: () => void;
  defaultDate?: Date | null;
  onOpenVideoMeeting?: (date?: Date | null) => void;
  defaultContact?: { id: string; name: string } | null;
  defaultDeal?: { id: string; name: string } | null;
}

const TYPE_OPTIONS: { value: TaskItemType; label: string; icon: React.ReactNode }[] = [
  { value: 'meeting_video', label: 'Reunião Video', icon: <Video className="w-4 h-4" /> },
  { value: 'call', label: 'Ligação', icon: <Phone className="w-4 h-4" /> },
  { value: 'whatsapp', label: 'Whatsapp', icon: <MessageCircle className="w-4 h-4" /> },
  { value: 'task', label: 'Tarefa', icon: <CheckSquare className="w-4 h-4" /> },
  { value: 'lembrete', label: 'Lembrete', icon: <Bell className="w-4 h-4" /> },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Normal' },
  { value: 'low', label: 'Baixa' },
];

function getDefaultStartEnd(defaultDate?: Date | null) {
  const base = defaultDate ? new Date(defaultDate) : new Date();
  // Round to next hour
  base.setMinutes(0, 0, 0);
  if (!defaultDate) base.setHours(base.getHours() + 1);
  const end = addHours(base, 1);
  return {
    start: format(base, "yyyy-MM-dd'T'HH:mm"),
    end: format(end, "yyyy-MM-dd'T'HH:mm"),
  };
}

export function TaskFormModal({ open, onClose, defaultDate, onOpenVideoMeeting: _unusedVideoMeeting, defaultContact, defaultDeal }: TaskFormModalProps) {
  const { data: currentUser } = useCurrentAppUser();
  const { users } = useActiveAppUsersForSelect();
  const queryClient = useQueryClient();
  const { createEvent } = useCalendarEventMutations();

  const [itemType, setItemType] = useState<TaskItemType>('task');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [dueDate, setDueDate] = useState(defaultDate ? format(defaultDate, 'yyyy-MM-dd') : '');
  const [withTime, setWithTime] = useState(false);
  const [dueTime, setDueTime] = useState('09:00');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Meeting video states
  const [meetingStartAt, setMeetingStartAt] = useState('');
  const [meetingEndAt, setMeetingEndAt] = useState('');
  const [meetingAllDay, setMeetingAllDay] = useState(false);
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);

  // Contact search
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<{ id: string; label: string } | null>(null);
  const { contacts, loading: loadingContacts } = useContactSearch(contactSearch);

  // Deal search (free mode — no contact selected)
  const [dealSearch, setDealSearch] = useState('');
  const [selectedDeal, setSelectedDeal] = useState<{ id: string; label: string } | null>(null);
  const { results: deals, loading: loadingDeals } = useDealsSearch(dealSearch, {
    enabled: !selectedContact,
  });

  // Deals by contact (filtered mode — contact selected)
  const { data: contactDeals = [], isLoading: loadingContactDeals } = useQuery({
    queryKey: ['deals-by-contact', selectedContact?.id],
    queryFn: async () => {
      if (!selectedContact?.id) return [];
      const { data, error } = await supabase
        .from('deals')
        .select('id, name, contact_id')
        .eq('contact_id', selectedContact.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedContact?.id,
  });

  // Set default assigned to current user
  React.useEffect(() => {
    if (currentUser?.id && !assignedTo) {
      setAssignedTo(currentUser.id);
    }
  }, [currentUser?.id]);

  // Update dueDate when defaultDate changes
  React.useEffect(() => {
    if (defaultDate) setDueDate(format(defaultDate, 'yyyy-MM-dd'));
  }, [defaultDate]);

  // Initialize meeting start/end when switching to meeting_video
  React.useEffect(() => {
    if (itemType === 'meeting_video' && !meetingStartAt) {
      const defaults = getDefaultStartEnd(defaultDate);
      setMeetingStartAt(defaults.start);
      setMeetingEndAt(defaults.end);
    }
  }, [itemType]);

  // Pre-fill contact and deal from props
  React.useEffect(() => {
    if (open && defaultContact && !selectedContact) {
      setSelectedContact({ id: defaultContact.id, label: defaultContact.name });
    }
    if (open && defaultDeal && !selectedDeal) {
      setSelectedDeal({ id: defaultDeal.id, label: defaultDeal.name });
    }
  }, [open, defaultContact, defaultDeal]);

  const handleClearContact = () => {
    setSelectedContact(null);
    setContactSearch('');
    if (!defaultDeal) {
      setSelectedDeal(null);
      setDealSearch('');
    }
  };

  const handleSelectDealFree = (deal: any) => {
    const dealLabel = deal.name || deal.id;
    setSelectedDeal({ id: deal.id, label: dealLabel });
    setDealSearch('');
    if (!defaultContact && deal.contact && !selectedContact) {
      const c = deal.contact;
      const contactLabel = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.primary_email || c.primary_phone || c.id;
      setSelectedContact({ id: c.id, label: contactLabel });
    }
  };

  const handleSelectDealFromContact = (deal: any) => {
    const dealLabel = deal.name || deal.id;
    setSelectedDeal({ id: deal.id, label: dealLabel });
  };

  const handleTypeChange = (type: TaskItemType) => {
    setItemType(type);
  };

  const toggleAttendee = (userId: string) => {
    setAttendeeIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const reset = () => {
    setItemType('task');
    setTitle('');
    setPriority('medium');
    setAssignedTo(currentUser?.id || '');
    setDueDate(defaultDate ? format(defaultDate, 'yyyy-MM-dd') : '');
    setWithTime(false);
    setDueTime('09:00');
    setDescription('');
    setContactSearch('');
    setSelectedContact(null);
    setDealSearch('');
    setSelectedDeal(null);
    setMeetingStartAt('');
    setMeetingEndAt('');
    setMeetingAllDay(false);
    setAttendeeIds([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Título é obrigatório.');
      return;
    }

    const isMeetingVideo = itemType === 'meeting_video';
    const isCallType = itemType === 'call';

    if (!isMeetingVideo && !dueDate) {
      toast.error('Data é obrigatória.');
      return;
    }

    if (isMeetingVideo && !meetingAllDay && (!meetingStartAt || !meetingEndAt)) {
      toast.error('Início e fim são obrigatórios.');
      return;
    }

    setSaving(true);
    try {
      if (isMeetingVideo) {
        // Create calendar event
        const startISO = meetingAllDay
          ? new Date(`${meetingStartAt.split('T')[0]}T00:00:00`).toISOString()
          : new Date(meetingStartAt).toISOString();
        const endISO = meetingAllDay
          ? new Date(`${(meetingEndAt || meetingStartAt).split('T')[0]}T23:59:59`).toISOString()
          : new Date(meetingEndAt).toISOString();

        const calendarData: any = {
          title: title.trim(),
          description: description.trim() || null,
          start_at: startISO,
          end_at: endISO,
          all_day: meetingAllDay,
          type: 'meeting',
          status: 'scheduled',
          color: null,
          created_by: currentUser?.id || null,
          attendee_ids: attendeeIds,
          contact_id: selectedContact?.id || null,
          deal_id: selectedDeal?.id || null,
          daily_room_url: null,
          daily_room_name: null,
        };

        // Create calendar event first so we have an ID to bind the Daily room to
        const createdEvent: any = await createEvent.mutateAsync(calendarData);
        const calendarEventId = createdEvent?.id;

        // Provision Daily room (skips if event already has one — idempotent in edge fn).
        // For all-day meetings we still create a room so the link exists.
        let roomUrl: string | null = null;
        let roomName: string | null = null;
        try {
          const { data: roomData, error: roomError } = await supabase.functions.invoke(
            'create-daily-room',
            {
              body: {
                calendar_event_id: calendarEventId,
                contact_id: selectedContact?.id || null,
                deal_id: selectedDeal?.id || null,
                consultant_id: currentUser?.id || null,
                attendee_ids: attendeeIds,
                end_at: endISO,
                type: 'scheduled',
              },
            },
          );
          if (roomError) {
            console.warn('[TaskFormModal] create-daily-room failed:', roomError.message);
          } else if (roomData?.room_url) {
            roomUrl = roomData.room_url;
            roomName = roomData.room_name ?? null;
          }
        } catch (roomErr: any) {
          console.warn('[TaskFormModal] create-daily-room threw:', roomErr?.message);
        }

        // Mirror to calls table — INCLUDING meeting_url so post-call sync (status,
        // recording, transcript) can find this row via daily-webhook / transcribe-meeting.
        const { error: callError } = await supabase.from('calls').insert({
          title: title.trim(),
          scheduled_for: startISO,
          handled_by_app_user_id: currentUser?.id || null,
          contact_id: selectedContact?.id || null,
          deal_id: selectedDeal?.id || null,
          status: 'scheduled',
          channel: 'video',
          direction: 'outbound',
          provider: 'daily',
          created_by: currentUser?.id || null,
          scheduled_by: currentUser?.id || null,
          attendees: attendeeIds.length > 0 ? JSON.stringify(attendeeIds) : null,
          meeting_url: roomUrl,
          metadata: roomName ? { daily_room_name: roomName, calendar_event_id: calendarEventId } : { calendar_event_id: calendarEventId },
        });
        if (callError) console.error('Mirror to calls failed:', callError);

        queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
        queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
        toast.success('Reunião agendada com sucesso.');
      } else if (isCallType) {
        const scheduledFor = withTime
          ? new Date(`${dueDate}T${dueTime}`).toISOString()
          : `${dueDate}T12:00:00Z`;

        let contactPhone: string | null = null;
        if (selectedContact?.id) {
          const { data: cData } = await supabase
            .from('contacts')
            .select('primary_phone')
            .eq('id', selectedContact.id)
            .maybeSingle();
          contactPhone = (cData as any)?.primary_phone || null;
        }

        const { error } = await supabase.from('calls').insert({
          title: title.trim(),
          scheduled_for: scheduledFor,
          handled_by_app_user_id: assignedTo || currentUser?.id || null,
          contact_id: selectedContact?.id || null,
          deal_id: selectedDeal?.id || null,
          status: 'scheduled',
          channel: 'voice',
          direction: 'outbound',
          provider: 'manual',
          created_by: assignedTo || currentUser?.id || null,
          scheduled_by: currentUser?.id || null,
          to_e164: contactPhone,
          metadata: !withTime ? { all_day: true } : {},
        });
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
        queryClient.invalidateQueries({ queryKey: ['scheduled-calls-by-contact'] });
        toast.success('Ligação agendada com sucesso.');
      } else {
        const { error } = await supabase.from('tasks').insert({
          title: title.trim(),
          description: description.trim() || null,
          priority,
          status: 'pending',
          assigned_to: assignedTo || currentUser?.id || null,
          created_by: currentUser?.id || null,
          due_date: dueDate,
          due_time: withTime ? `${dueTime}:00` : null,
          deal_id: selectedDeal?.id || null,
          contact_id: selectedContact?.id || null,
          metadata: {
            type: itemType,
          },
        });
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
        queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
        queryClient.invalidateQueries({ queryKey: ['deal-tasks-timeline'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        toast.success('Tarefa criada com sucesso.');
      }
      handleClose();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao criar item.');
    } finally {
      setSaving(false);
    }
  };

  const isCallType = itemType === 'call';
  const isMeetingVideo = itemType === 'meeting_video';
  const isContactLocked = !!defaultDeal && !!selectedContact;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nova Atividade</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Type selector */}
          <div>
            <Label>Tipo</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleTypeChange(opt.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    itemType === opt.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-accent'
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label>Título *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isMeetingVideo ? 'Assunto da reunião' : isCallType ? 'Assunto da ligação' : 'Título da tarefa'}
              required
            />
          </div>

          {/* Priority — only for non-call, non-meeting types */}
          {!isCallType && !isMeetingVideo && (
            <div>
              <Label>Prioridade</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Meeting Video: Start/End + All Day */}
          {isMeetingVideo && (
            <>
              <div className="flex items-center gap-3">
                <Switch id="all-day" checked={meetingAllDay} onCheckedChange={setMeetingAllDay} />
                <Label htmlFor="all-day" className="cursor-pointer">Dia inteiro</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Início *</Label>
                  <Input
                    type={meetingAllDay ? 'date' : 'datetime-local'}
                    value={meetingAllDay ? meetingStartAt.split('T')[0] : meetingStartAt}
                    onChange={(e) => setMeetingStartAt(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Fim *</Label>
                  <Input
                    type={meetingAllDay ? 'date' : 'datetime-local'}
                    value={meetingAllDay ? (meetingEndAt || meetingStartAt).split('T')[0] : meetingEndAt}
                    onChange={(e) => setMeetingEndAt(e.target.value)}
                    required
                  />
                </div>
              </div>
            </>
          )}

          {/* Date + Assignee row — for non-meeting types */}
          {!isMeetingVideo && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Responsável</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Time toggle — for non-meeting types */}
          {!isMeetingVideo && (
            <div className="flex items-center gap-3">
              <Switch id="with-time" checked={withTime} onCheckedChange={setWithTime} />
              <Label htmlFor="with-time" className="cursor-pointer">Definir horário?</Label>
              {withTime && (
                <Input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="w-28"
                />
              )}
            </div>
          )}

      {/* Participants — meeting_video only */}
      {isMeetingVideo && (
        <div>
          <Label>Participantes</Label>
          <div className="mt-1.5 border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
            {users
              .filter((u, idx, arr) => {
                const nameOf = (x: typeof u) =>
                  (x.display_name || [x.first_name, x.last_name].filter(Boolean).join(' ') || x.email || '').trim().toLowerCase();
                const key = nameOf(u);
                if (!key) return true;
                return arr.findIndex((x) => nameOf(x) === key) === idx;
              })
              .map((u) => {
              const name = u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
              const isChecked = attendeeIds.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-accent cursor-pointer text-sm">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleAttendee(u.id)}
                      />
                      {name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contact search */}
          <div>
            <Label>Contato (opcional)</Label>
            {selectedContact ? (
              <div className="flex items-center gap-2 mt-1.5 p-2 border rounded-md bg-muted/40">
                <span className="flex-1 text-sm">{selectedContact.label}</span>
                {!defaultContact && !isContactLocked && (
                  <button
                    type="button"
                    onClick={handleClearContact}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="relative mt-1.5">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Buscar contato..."
                  className="pl-8"
                />
                {loadingContacts && (
                  <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {contacts.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto">
                    {contacts.map((c) => {
                      const label = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.primary_email || c.primary_phone || c.id;
                      const subtitle = c.primary_phone || c.primary_email || '';
                      return (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                          onClick={() => { setSelectedContact({ id: c.id, label }); setContactSearch(''); }}
                        >
                          <div className="font-medium">{label}</div>
                          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deal search — correlated with contact */}
          <div>
            <Label>Negociação (opcional)</Label>
            {selectedDeal ? (
              <div className="flex items-center gap-2 mt-1.5 p-2 border rounded-md bg-muted/40">
                <span className="flex-1 text-sm">{selectedDeal.label}</span>
                {!defaultDeal && (
                  <button
                    type="button"
                    onClick={() => { setSelectedDeal(null); setDealSearch(''); }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : selectedContact ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full justify-between mt-1.5"
                    disabled={loadingContactDeals}
                  >
                    <span className="text-muted-foreground truncate">
                      {loadingContactDeals
                        ? 'Carregando...'
                        : contactDeals.length === 0
                          ? 'Nenhuma negociação encontrada'
                          : 'Selecionar negociação...'}
                    </span>
                    {loadingContactDeals ? (
                      <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    )}
                  </Button>
                </PopoverTrigger>
                {contactDeals.length > 0 && (
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <div className="max-h-48 overflow-y-auto">
                      {contactDeals.map((d: any) => (
                        <button
                          key={d.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b last:border-b-0"
                          onClick={() => handleSelectDealFromContact(d)}
                        >
                          {d.name || d.id}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                )}
              </Popover>
            ) : (
              <div className="relative mt-1.5">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  value={dealSearch}
                  onChange={(e) => setDealSearch(e.target.value)}
                  placeholder="Buscar negociação..."
                  className="pl-8"
                />
                {loadingDeals && (
                  <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {deals.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto">
                    {deals.map((d: any) => {
                      const label = d.name || d.id;
                      const contactName = d.contact
                        ? [d.contact.first_name, d.contact.last_name].filter(Boolean).join(' ')
                        : '';
                      return (
                        <button
                          key={d.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                          onClick={() => handleSelectDealFree(d)}
                        >
                          <div className="font-medium">{label}</div>
                          {contactName && <div className="text-xs text-muted-foreground">{contactName}</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          {!isCallType && (
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrição opcional..."
                rows={3}
              />
            </div>
          )}

          </div>

          <DialogFooter className="pt-4 border-t mt-4 shrink-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !currentUser}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isMeetingVideo ? 'Agendar Reunião' : isCallType ? 'Agendar Ligação' : 'Criar Tarefa'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
